import React, { useState, useEffect } from 'react';
import { BaseUrl } from '../../BaseUrl';

const API_BASE = `${BaseUrl}/api/v1`;

const FLAG_REASONS = ['Link broken', 'Product unavailable', 'Inappropriate', 'Spam'];

const ProductDetailModal = ({
  productId,
  onClose,
  onProductClick,
  getCategoryImage,
  getCategoryColor,
  isLiked,
  toggleLike,
  getLikeCount,
}) => {
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showFlagForm, setShowFlagForm] = useState(false);
  const [flagReason, setFlagReason] = useState('');
  const [flagComment, setFlagComment] = useState('');
  const [flagSubmitting, setFlagSubmitting] = useState(false);
  const [flagResult, setFlagResult] = useState(null); // { success, message }

  useEffect(() => {
    if (!productId) return;
    setLoading(true);

    fetch(`${API_BASE}/goods/community/${productId}`)
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          setProduct(data.data);
        }
      })
      .catch(err => console.error('Error fetching product detail:', err))
      .finally(() => setLoading(false));
  }, [productId]);

  // Prevent body scroll while modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const handleViewProduct = async () => {
    if (!product) return;
    try {
      await fetch(`${API_BASE}/goods/community/${product._id}/click`, { method: 'POST' });
    } catch (error) {
      // Silently fail
    }
    const link = product.link.startsWith('http') ? product.link : `https://${product.link}`;
    window.open(link, '_blank', 'noopener,noreferrer');
  };

  const handleRelatedClick = (relatedId) => {
    onProductClick(relatedId);
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const handleFlagSubmit = async () => {
    if (!flagReason) return;
    setFlagSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setFlagResult({ success: false, message: 'Please log in to flag a product.' });
        return;
      }
      const res = await fetch(`${API_BASE}/goods/community/${product._id}/flag`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ reason: flagReason, comment: flagComment.trim() }),
      });
      const data = await res.json();
      setFlagResult({
        success: data.success,
        message: data.success ? data.message : (data.error || 'Failed to flag product'),
      });
      if (data.success) {
        setShowFlagForm(false);
      }
    } catch {
      setFlagResult({ success: false, message: 'Something went wrong. Please try again.' });
    } finally {
      setFlagSubmitting(false);
    }
  };

  const categoryColor = product ? getCategoryColor(product.categories?.[0]) : '#6B3A5B';
  const categoryImg = product ? getCategoryImage(product.categories?.[0]) : null;

  // Comments: recommenders who left a description
  const comments = product?.recommenders?.filter(r => r.description && r.description.trim()) || [];

  return (
    <div className="pdm-overlay" onClick={handleBackdropClick}>
      <div className="pdm-modal">
        <button className="pdm-close" onClick={onClose}>
          <i className="bi bi-x-lg"></i>
        </button>

        {loading ? (
          <div className="pdm-loading">
            <div className="dg-spinner"></div>
            <p>Loading product details...</p>
          </div>
        ) : !product ? (
          <div className="pdm-loading">
            <p>Product not found.</p>
          </div>
        ) : (
          <div className="pdm-content">
            {/* Header */}
            <div className="pdm-header">
              <div className="pdm-header-icon">
                {categoryImg && <img src={categoryImg} alt={product.categories?.[0] || ''} className="pdm-header-icon-img" />}
              </div>
              <div className="pdm-header-info">
                <h2 className="pdm-title">{product.title}</h2>
                <div className="pdm-tags">
                  <span className="pdm-category" style={{ background: `${categoryColor}15`, color: categoryColor }}>
                    {product.categories?.[0] || 'General'}
                  </span>
                  {product.brand && (
                    <span className="pdm-brand">{product.brand}</span>
                  )}
                </div>
              </div>
            </div>

            <div className="pdm-divider" style={{ background: categoryColor }} />

            {/* Social proof */}
            <div className="pdm-social">
              <i className="bi bi-people-fill"></i>
              <span>
                {product.recommendationCount} dancer{product.recommendationCount !== 1 ? 's' : ''} recommend{product.recommendationCount === 1 ? 's' : ''} this
              </span>
            </div>

            {/* Comments Section */}
            {comments.length > 0 && (
              <div className="pdm-comments-section">
                <h3 className="pdm-section-title">What dancers are saying</h3>
                <div className="pdm-comments-list">
                  {comments.map((r, idx) => (
                    <div key={idx} className="pdm-comment">
                      <div className="pdm-comment-body">
                        <p className="pdm-comment-text">"{r.description}"</p>
                        <div className="pdm-comment-meta">
                          <span className="pdm-comment-author">
                            — {r.alias || r.name?.split(' ')[0] || 'A dancer'}
                          </span>
                          {r.addedAt && (
                            <span className="pdm-comment-date">
                              {new Date(r.addedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* CTA */}
            <div className="pdm-actions-row">
              <button className="pdm-view-btn" onClick={handleViewProduct} style={{ background: categoryColor }}>
                View Product <i className="bi bi-box-arrow-up-right"></i>
              </button>
              <button
                className={`pdm-like-btn ${isLiked?.(product._id) ? 'liked' : ''}`}
                onClick={() => toggleLike?.(product._id)}
                title={isLiked?.(product._id) ? 'Remove from saved' : 'Save to library'}
              >
                <i className={isLiked?.(product._id) ? 'bi bi-heart-fill' : 'bi bi-heart'}></i>
              </button>
              {!flagResult?.success && (
                <button
                  className="pdm-flag-btn"
                  onClick={() => setShowFlagForm(!showFlagForm)}
                  title="Report an issue with this product"
                >
                  <i className="bi bi-flag"></i>
                </button>
              )}
            </div>

            {/* Flag result message */}
            {flagResult && (
              <div className={`pdm-flag-result ${flagResult.success ? 'success' : 'error'}`}>
                <i className={`bi ${flagResult.success ? 'bi-check-circle' : 'bi-exclamation-circle'}`}></i>
                {flagResult.message}
              </div>
            )}

            {/* Flag form */}
            {showFlagForm && !flagResult?.success && (
              <div className="pdm-flag-form">
                <p className="pdm-flag-label">What's wrong with this product?</p>
                <div className="pdm-flag-reasons">
                  {FLAG_REASONS.map(reason => (
                    <button
                      key={reason}
                      type="button"
                      className={`pdm-flag-reason ${flagReason === reason ? 'active' : ''}`}
                      onClick={() => setFlagReason(reason)}
                    >
                      {reason}
                    </button>
                  ))}
                </div>
                <textarea
                  className="pdm-flag-comment"
                  placeholder="Additional details (optional)"
                  value={flagComment}
                  onChange={(e) => setFlagComment(e.target.value)}
                  maxLength={200}
                  rows={2}
                />
                <button
                  className="pdm-flag-submit"
                  onClick={handleFlagSubmit}
                  disabled={!flagReason || flagSubmitting}
                >
                  {flagSubmitting ? 'Submitting...' : 'Submit Report'}
                </button>
              </div>
            )}

            {/* Related Products */}
            {product.relatedByBrand?.length > 0 && (
              <div className="pdm-related-section">
                <h3 className="pdm-section-title">More from {product.brand}</h3>
                <div className="pdm-related-grid">
                  {product.relatedByBrand.map(rel => (
                    <div
                      key={rel._id}
                      className="pdm-related-card"
                      onClick={() => handleRelatedClick(rel._id)}
                    >
                      <div className="pdm-related-icon">
                        {getCategoryImage(rel.categories?.[0]) && (
                          <img src={getCategoryImage(rel.categories?.[0])} alt="" className="pdm-related-icon-img" />
                        )}
                      </div>
                      <div className="pdm-related-info">
                        <span className="pdm-related-title">{rel.title}</span>
                        <span className="pdm-related-recs">
                          <i className="bi bi-people-fill"></i> {rel.recommendationCount}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductDetailModal;
